import { API } from '@/App';

/**
 * Inherit a city's pin onto a derived entity (Hotel / Activity / Guide /
 * Driver). Called from a LocationAutocomplete's `onSelect` when the
 * operator picks a saved city. If the city has coords and the entity has
 * a name, POSTs to /pollen/locations/pin-by-name so the entity's row in
 * pollen_locations adopts those coords. Result: one pin per city covers
 * every derived entity based there.
 *
 * Returns `{ lat, lng, google_maps_url, city, inherited: true }` on success,
 * a hint object with `inherited: false` and `reason` otherwise (so the
 * caller can surface a guiding toast).
 */
export async function inheritCityPin({ cityLoc, entityName, entityType }) {
  if (!entityName || !String(entityName).trim()) {
    return { inherited: false, reason: 'no-entity-name' };
  }
  if (!entityType) {
    return { inherited: false, reason: 'no-entity-type' };
  }
  if (cityLoc.lat == null || cityLoc.lng == null) {
    return { inherited: false, reason: 'no-city-coords' };
  }
  const url = cityLoc.google_maps_url || `${cityLoc.lat}, ${cityLoc.lng}`;
  try {
    const t = localStorage.getItem('session_token');
    const res = await fetch(`${API}/pollen/locations/pin-by-name`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify({
        name: String(entityName).trim(),
        type: String(entityType).toLowerCase(),
        url,
        city: cityLoc.name,
        inherit: true,
      }),
    });
    if (!res.ok) return { inherited: false, reason: 'pin-failed' };
    const data = await res.json();
    if (data.already_pinned) {
      // The entity already has its own coordinates — backend kept them.
      return {
        inherited: false,
        already_pinned: true,
        lat: data.lat,
        lng: data.lng,
        verified: !!data.verified,
        google_maps_url: data.google_maps_url || '',
        city: cityLoc.name,
      };
    }
    return {
      inherited: true,
      lat: data.lat,
      lng: data.lng,
      verified: false,
      google_maps_url: url,
      city: cityLoc.name,
    };
  } catch {
    return { inherited: false, reason: 'pin-error' };
  }
}
